import api from './api'
import type { ImpersonationInfo, ImpersonationLoginResponse, LoginCredentials, LoginResponse, RegisterData, RegisterResponse, User, UserInitResponse } from '../types/auth.types'

/**
 * Authentication Service
 * Handles all authentication-related API calls
 */
class AuthService {
  /**
   * Login user
   * POST v1/auth/login
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>('v1/auth/login', credentials)

    // Store token and user data
    if (response.data.access_token) {
      localStorage.removeItem('impersonation')
      this.setToken(response.data.access_token)
      this.setUser(response.data.user)
    }

    return response.data
  }

  /**
   * Initialize user data after login
   * POST v1/{user}/init
   * Fetches and stores all user-related data in localStorage
   */
  async initUser(userUuid: string): Promise<UserInitResponse> {
    const response = await api.post<UserInitResponse>(`v1/${userUuid}/init`)

    // Store all init data in localStorage
    this.setInitData(response.data)

    // Update user data if present
    if (response.data.user) {
      this.setUser(response.data.user)
    }

    return response.data
  }

  /**
   * Delete account
   * DELETE v1/auth/account
   */
  async deleteAccount(password: string): Promise<void> {
    await api.delete('v1/auth/account', { data: { password } })
    this.clearAuth()
  }

  /**
   * Logout user
   * POST v1/auth/logout
   */
  async logout(): Promise<void> {
    try {
      await api.post('v1/auth/logout')
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // Always clear local storage
      this.clearAuth()
    }
  }

  /**
   * Exchange the one-time code from the admin panel ("Zaloguj się") for the user's token
   * POST v1/auth/impersonate
   */
  async impersonate(code: string): Promise<ImpersonationLoginResponse> {
    const response = await api.post<ImpersonationLoginResponse>('v1/auth/impersonate', { code })
    return response.data
  }

  /**
   * End the admin session without logging the user out on their own devices
   * POST v1/auth/impersonation/end
   */
  async endImpersonation(): Promise<void> {
    try {
      await api.post('v1/auth/impersonation/end')
    } catch (error) {
      console.error('End impersonation error:', error)
    } finally {
      this.clearAuth()
    }
  }

  /**
   * Register new user
   * POST v1/auth/register
   */
  async register(data: RegisterData): Promise<RegisterResponse> {
    const response = await api.post<RegisterResponse>('v1/auth/register', data)
    return response.data
  }

  /**
   * Get current user
   * GET v1/auth/me
   */
  async getCurrentUser(): Promise<User> {
    const response = await api.get<User>('v1/auth/me')
    this.setUser(response.data)
    return response.data
  }

  /**
   * Refresh token
   * POST v1/auth/refresh
   */
  async refreshToken(): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>('v1/auth/refresh')

    if (response.data.access_token) {
      this.setToken(response.data.access_token)
    }

    return response.data
  }

  /**
   * Activate account
   * POST v1/auth/activate
   */
  async activate(token: string, email: string): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>('v1/auth/activate', {
      token,
      email
    })
    return response.data
  }

  /**
   * Resend activation email
   * POST v1/auth/resend-activation
   */
  async resendActivation(email: string): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>('v1/auth/resend-activation', {
      email
    })
    return response.data
  }

  /**
   * Update user profile
   * PUT v1/auth/{user}/profile
   */
  async updateProfile(userUuid: string, data: any): Promise<any> {
    const response = await api.put(`v1/auth/${userUuid}/profile`, data)
    return response.data
  }

  // ===== Helper Methods =====

  /**
   * Store token in localStorage
   */
  setToken(token: string): void {
    localStorage.setItem('auth_token', token)
  }

  /**
   * Get token from localStorage
   */
  getToken(): string | null {
    return localStorage.getItem('auth_token')
  }

  /**
   * Store user data in localStorage
   */
  setUser(user: User): void {
    localStorage.setItem('user', JSON.stringify(user))
  }

  /**
   * Get user data from localStorage
   */
  getUser(): User | null {
    const userStr = localStorage.getItem('user')
    return userStr ? JSON.parse(userStr) : null
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getToken()
  }

  /**
   * Store init data in localStorage
   */
  setInitData(initData: UserInitResponse): void {
    localStorage.setItem('user_init_data', JSON.stringify(initData))
  }

  /**
   * Get init data from localStorage
   */
  getInitData(): UserInitResponse | null {
    const initDataStr = localStorage.getItem('user_init_data')
    return initDataStr ? JSON.parse(initDataStr) : null
  }

  /**
   * Admin session marker (who the admin is logged in as)
   */
  setImpersonation(info: ImpersonationInfo): void {
    localStorage.setItem('impersonation', JSON.stringify(info))
  }

  getImpersonation(): ImpersonationInfo | null {
    const value = localStorage.getItem('impersonation')
    if (!value) return null
    try {
      return JSON.parse(value)
    } catch {
      return null
    }
  }

  /**
   * Clear all authentication data
   */
  clearAuth(): void {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user')
    localStorage.removeItem('user_init_data')
    localStorage.removeItem('impersonation')
  }
}

export default new AuthService()
